import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, apiErrorMessage } from "@/lib/api";
import { CampaignCardContent } from "@/pages/Campaigns";
import { Star } from "lucide-react";

export function MyCampaignsPage() {
  const nav = useNavigate();
  const [campaigns, setCampaigns] = useState([]);
  const [favorites, setFavorites] = useState(new Set());
  const [loading, setLoading] = useState(true);

  async function load() {
    setLoading(true);
    try {
      const [joinedRes, favRes] = await Promise.all([
        api.get("/campaigns/joined"),
        api.get("/campaigns/favorites").catch(() => ({ data: [] })),
      ]);
      const favSet = new Set(favRes.data || []);
      setFavorites(favSet);
      setCampaigns(joinedRes.data || []);
    } catch {
      setCampaigns([]);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, []);

  async function toggleFavorite(campaignId) {
    try {
      const res = await api.post(`/campaigns/${campaignId}/favorite`);
      setFavorites((prev) => {
        const next = new Set(prev);
        if (res.data?.favorited) next.add(campaignId);
        else next.delete(campaignId);
        return next;
      });
    } catch { /* ignore */ }
  }

  return (
    <div data-testid="my-campaigns-page" className="space-y-6">
      <div>
        <h1 className="page-title">My Campaigns</h1>
        <p className="page-subtitle">Campaigns you've joined. Submit clips to start earning.</p>
      </div>

      {loading ? (
        <div className="campaign-card-grid" data-testid="my-campaigns-loading">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="overflow-hidden rounded-[18px] border border-border/60 bg-card/60">
              <div className="aspect-[16/11] w-full animate-pulse bg-zinc-200 dark:bg-zinc-800" />
              <div className="space-y-2.5 p-4">
                <div className="flex items-center gap-2">
                  <div className="h-7 w-7 animate-pulse rounded-full bg-zinc-200 dark:bg-zinc-800" />
                  <div className="h-3 w-24 animate-pulse rounded bg-zinc-200 dark:bg-zinc-800" />
                </div>
                <div className="h-4 w-4/5 animate-pulse rounded bg-zinc-200 dark:bg-zinc-800" />
                <div className="flex gap-2">
                  <div className="h-5 w-12 animate-pulse rounded bg-zinc-200 dark:bg-zinc-800" />
                  <div className="h-5 w-14 animate-pulse rounded bg-zinc-200 dark:bg-zinc-800" />
                  <div className="h-5 w-14 animate-pulse rounded bg-zinc-200 dark:bg-zinc-800" />
                </div>
              </div>
              <div className="h-1.5 w-full animate-pulse bg-zinc-200 dark:bg-zinc-800" />
            </div>
          ))}
        </div>
      ) : campaigns.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-border/60 px-6 py-12 text-center">
          <Star size={28} className="mx-auto text-muted-foreground/40" />
          <div className="mt-3 text-sm font-medium text-muted-foreground">No campaigns yet</div>
          <div className="mt-1 text-xs text-muted-foreground/60">Hit the <span className="font-bold text-[#c8ff00]">+</span> on any campaign card to add it here</div>
        </div>
      ) : (
        <div className="campaign-card-grid">
          {campaigns.map((c) => (
            <div key={c.id}>
              <CampaignCardContent
                c={c}
                canManage={false}
                isAdmin={false}
                joinStatus={true}
                isFav={favorites.has(c.id)}
                onToggleFav={toggleFavorite}
                onViewDetails={() => nav(`/campaigns/${c.id}`)}
                onJoin={() => {}}
                onEdit={() => {}}
                onRemove={() => {}}
              />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
